/** @format */

import React, { type FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getModeFromSessionStorage } from "senf-shared";
import theme from "../../../styles/theme";
import Box from "../../atoms/box/Box";
import Divider from "../../atoms/divider/Divider";
import Input from "../../atoms/inputs/Input";
import Typography from "../../atoms/typography/Typography";
import Button from "../../core/button/Button";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import useDataPrivacyShield from "../../special/auth/useDataPrivacyShield";
import type { AuthOptionsProps } from "./AuthOptions.types";

const AuthOptions: FC<AuthOptionsProps> = ({
  authHandler,
  setPage,
  reAuth,
  errorMessage,
  formikStore,
  project,
  heading,
  ...props
}) => {
  const { t } = useTranslation();
  const { closeModal } = useModals();
  const { handleDataPrivacy } = useDataPrivacyShield(project);

  const submitEmail = async () => {
    try {
      const response = await authHandler.userExists(formikStore);

      if (response.userId) {
        return closeModal();
      }
      if (response.length && response.includes("google.com")) {
        return closeModal();
      }
      if (response.length && response.includes("facebook.com")) {
        return closeModal();
      }

      if (response.length && response.includes("password")) {
        setPage("authEmail");
      } else {
        setPage("authEmailRegister");
      }
      return response;
    } catch (error) {
      throw new Error(`Error:${error}`);
    }
  };

  return (
    <Box
      flexDirection="column"
      margin="150px 10% 0px 10%"
      position="relative"
      zIndex={9999}
    >
      {getModeFromSessionStorage() === "workshop" ? (
        <>
          <Typography variant="h1" style={{ position: "relative" }}>
            {t("auth:authOptions.titleLine1")}
          </Typography>
          <Typography variant="h1" style={{ position: "relative" }}>
            {t("auth:authOptions.titleLine2")}
          </Typography>

          <Box margin="105px 0px 24px 0px">
            {/* <Typography variant="buttonBg">
              Teile deine Ideen mit anderen, trete in den Dialog und unterstütze
              Ideen, die dir gefallen. Fülle Umfragen aus und beeinflusse so
              aktiv das Geschehen in deiner Stadt.
            </Typography> */}
          </Box>

          <Button
            variant="white"
            width="max"
            size="xl"
            classNameWrapper="pb-10"
            text={t("global:letsGo")}
            loading={!!authHandler?.loading?.anonymous}
            onClick={() => {
              authHandler.signIn.anonymous(formikStore);
            }}
          />
        </>
      ) : (
        <>
          {!reAuth ? (
            <>
              <Typography variant="h1" style={{ position: "relative" }}>
                {heading?.titleLine1 || t("auth:authOptions.titleLine1")}
              </Typography>
              <Typography variant="h1" style={{ position: "relative" }}>
                {heading?.titleLine2 || t("auth:authOptions.titleLine2")}
              </Typography>

              <Box margin="25px 0px 24px 0px">
                <Typography variant="bodyBg" style={{ position: "relative" }}>
                  {heading?.description || t("auth:authOptions.subtitle")}
                </Typography>
              </Box>
            </>
          ) : (
            <Box marginBottom="100px" flexDirection="column" gap="10px">
              <Typography variant="h3">
                {t("auth:authOptions.reAuthTitle")}
              </Typography>
              <Typography variant="bodySm">
                {t("auth:authOptions.reAuthSubtitle")}
              </Typography>
            </Box>
          )}
          <Box gap="16px" flexDirection="column">
            {project?.settings?.registration !== false && (
              <>
                <Button
                  variant="white"
                  width="max"
                  size="lg"
                  pillShaped={false}
                  text={t("auth:authOptions.googleLogin")}
                  leadingIcon="logo/Google"
                  loading={!!authHandler?.loading?.google}
                  onClick={async () => {
                    const privacyAccepted = await handleDataPrivacy();

                    if (privacyAccepted) {
                      authHandler?.signIn.google(formikStore);
                    }
                  }}
                />

                {/*         <Button
                  variant="white"
                  width="max"
                  size="lg"
                  pillShaped={false}
                  text={t("auth:authOptions.facebookLogin")}
                  loading={!!authHandler?.loading?.facebook}
                  leadingIcon="Facebook"
                  onClick={async () => {
                    const privacyAccepted = await handleDataPrivacy();

                    if (privacyAccepted) {
                      authHandler?.signIn.facebook(formikStore);
                    }
                  }}
                /> */}
              </>
            )}
            {project?.settings?.guestLogin === true && (
              <Button
                variant="white"
                width="max"
                size="lg"
                pillShaped={false}
                text={t("auth:authOptions.anonymousLogin")}
                loading={!!authHandler?.loading?.anonymous}
                onClick={async () => {
                  const privacyAccepted = await handleDataPrivacy();
                  if (privacyAccepted) {
                    authHandler.signIn.anonymous(formikStore);
                  }
                }}
              />
            )}

            {errorMessage && (
              <Typography variant="bodySm" color={theme.colors.signal.redDark}>
                {t(errorMessage)}
              </Typography>
            )}
          </Box>
          {project?.settings?.registration !== false && (
            <>
              <Box marginBlock="1rem">
                <Divider
                  classNames={{
                    divider: "bg-text-on-primary opacity-10",
                    text: "text-text-on-primary opacity-30",
                  }}
                >
                  {t("auth:authOptions.orContinueWithEmail")}
                </Divider>
              </Box>
              <Box gap="16px" flexDirection="column">
                <Input
                  data-cy="email"
                  name="email"
                  type="mail"
                  placeholder={t("auth:email")}
                  onChange={formikStore?.handleChange}
                  onBlur={formikStore?.handleBlur}
                  value={formikStore?.values.email}
                  error={
                    formikStore?.touched.email &&
                    Boolean(formikStore?.errors.email)
                  }
                  variant="white"
                  leadingIcon="mail"
                  trailingIcon={
                    formikStore?.isValid
                      ? authHandler?.loading?.email
                        ? "loading"
                        : document.dir === "rtl"
                          ? "arrowhead-left-md"
                          : "arrowhead-right-md"
                      : undefined
                  }
                  trailingIconLabel={t("global:next")}
                  trailingIconClick={submitEmail}
                  trailingIconCy="email-continue"
                  onSubmit={submitEmail}
                />
                <Input
                  data-cy="password"
                  name="password"
                  type="password"
                  hidden={true}
                  onChange={formikStore?.handleChange}
                  onBlur={formikStore?.handleBlur}
                  value={formikStore?.values.password}
                />
              </Box>
            </>
          )}
          <div className="body-sm text-text-on-primary my-6 opacity-75">
            <Trans i18nKey="auth:terms">
              ...
              <Link
                to={
                  props?.app === "dashboard"
                    ? "/terms"
                    : `/projects/${project?.projectId}/terms`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-on-primary font-bold underline"
              ></Link>
              ...
              <Link
                to={
                  props?.app === "dashboard"
                    ? "/privacyPolicy"
                    : `/projects/${project?.projectId}/privacyPolicy`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-on-primary font-bold underline"
              ></Link>
              ...
            </Trans>
          </div>
        </>
      )}
    </Box>
  );
};

export default AuthOptions;
